import {
  archiveContract as archiveContractApi,
  createContract as createContractApi,
  getContract as getContractApi,
  listContracts as listContractsApi,
  listContractLineItems as listContractLineItemsApi,
  listContractRevenueSchedules as listContractRevenueSchedulesApi,
  patchContract as patchContractApi,
  restoreContract as restoreContractApi,
  transitionContract as transitionContractApi,
} from '@shared/api/contracts';
import { type Paged, parsePaged } from '@shared/api/paging';
import type {
  ContractCreateRequest,
  ContractLineItem,
  ContractListQuery,
  ContractPatchRequest,
  TransitionContractRequest,
} from '@shared/types/contract';
import type { Contract } from '@domain/contract/model';
import { parseContract } from '@domain/contract/parseContract';
import { sumScheduleRevenue } from '@domain/contract/line-items';

interface ListState {
  data: Paged<Contract> | null;
  loading: boolean;
  error: string | null;
}

const listState = $state<ListState>({ data: null, loading: false, error: null });
let listToken = 0;

export const contractListStore = {
  get data(): Paged<Contract> | null {
    return listState.data;
  },
  get loading(): boolean {
    return listState.loading;
  },
  get error(): string | null {
    return listState.error;
  },
  async refresh(query: ContractListQuery): Promise<void> {
    const token = ++listToken;
    // Clear the previous result so stale rows from a different scope
    // (e.g. the firm-detail tab vs. the cross-firm `/contracts` page) don't
    // flash through while the new query is in flight.
    listState.data = null;
    listState.loading = true;
    listState.error = null;
    try {
      const raw = await listContractsApi(query);
      if (token !== listToken) return;
      listState.data = parsePaged(raw, parseContract);
    } catch (err) {
      if (token !== listToken) return;
      listState.error = err instanceof Error ? err.message : String(err);
    } finally {
      if (token === listToken) listState.loading = false;
    }
  },
};

interface DetailState {
  data: Contract | null;
  loading: boolean;
  error: string | null;
}

const detailState = $state<DetailState>({ data: null, loading: false, error: null });
let detailToken = 0;

export const contractDetailStore = {
  get data(): Contract | null {
    return detailState.data;
  },
  get loading(): boolean {
    return detailState.loading;
  },
  get error(): string | null {
    return detailState.error;
  },
  async load(id: string): Promise<void> {
    const token = ++detailToken;
    detailState.loading = true;
    detailState.error = null;
    try {
      const raw = await getContractApi(id);
      if (token !== detailToken) return;
      detailState.data = parseContract(raw);
    } catch (err) {
      if (token !== detailToken) return;
      detailState.error = err instanceof Error ? err.message : String(err);
      detailState.data = null;
    } finally {
      if (token === detailToken) detailState.loading = false;
    }
  },
  setLocal(contract: Contract): void {
    detailState.data = contract;
  },
  clear(): void {
    detailToken++;
    detailState.data = null;
    detailState.error = null;
    detailState.loading = false;
  },
};

/// 契約の明細 (line-items + revenue-schedules) をキャッシュするストア。
///
/// ContractLineItemsEditor でのデータ取得に使う read/write ストア。
/// mutation (create / patch / delete) は component 側で直接 API を呼び、
/// 成功後に `setItems` / `updateItem` / `removeItem` / `appendSchedule` /
/// `updateSchedule` / `removeSchedule` で state を同期する。
interface LineItemsState {
  data: ContractLineItem[] | null;
  loading: boolean;
  error: string | null;
  loadedContractId: string | null;
}

const lineItemsState = $state<LineItemsState>({
  data: null,
  loading: false,
  error: null,
  loadedContractId: null,
});
let lineItemsToken = 0;

export const contractLineItemsStore = {
  get data(): ContractLineItem[] | null {
    return lineItemsState.data;
  },
  get loading(): boolean {
    return lineItemsState.loading;
  },
  get error(): string | null {
    return lineItemsState.error;
  },
  get loadedContractId(): string | null {
    return lineItemsState.loadedContractId;
  },
  /// 指定契約の明細とスケジュールを全件取得する。stale は token で破棄。
  async load(contractId: string): Promise<void> {
    const token = ++lineItemsToken;
    lineItemsState.loading = true;
    lineItemsState.error = null;
    try {
      const res = await listContractLineItemsApi(contractId);
      const itemsWithSchedules = await Promise.all(
        res.items.map(async (item) => {
          const schedules = await listContractRevenueSchedulesApi(
            contractId,
            item.id,
          );
          return { ...item, schedules: schedules.items };
        }),
      );
      if (token !== lineItemsToken) return;
      lineItemsState.data = itemsWithSchedules;
      lineItemsState.loadedContractId = contractId;
    } catch (err) {
      if (token !== lineItemsToken) return;
      lineItemsState.error = err instanceof Error ? err.message : String(err);
      lineItemsState.data = [];
      lineItemsState.loadedContractId = contractId;
    } finally {
      if (token === lineItemsToken) lineItemsState.loading = false;
    }
  },
  setItems(items: ContractLineItem[]): void {
    lineItemsState.data = items;
  },
  updateItem(id: string, patch: Partial<ContractLineItem>): void {
    lineItemsState.data =
      lineItemsState.data?.map((item) =>
        item.id === id ? { ...item, ...patch } : item,
      ) ?? null;
  },
  appendItem(item: ContractLineItem): void {
    lineItemsState.data = [
      ...(lineItemsState.data ?? []),
      { ...item, schedules: item.schedules ?? [] },
    ];
  },
  removeItem(id: string): void {
    lineItemsState.data =
      lineItemsState.data?.filter((item) => item.id !== id) ?? null;
  },
  updateSchedule(
    lineItemId: string,
    scheduleId: string,
    patch: Partial<ContractLineItem['schedules'][number]>,
  ): void {
    lineItemsState.data =
      lineItemsState.data?.map((item) => {
        if (item.id !== lineItemId) return item;
        const schedules = item.schedules.map((s) =>
          s.id === scheduleId ? { ...s, ...patch } : s,
        );
        return {
          ...item,
          schedules,
          schedule_revenue_total: sumScheduleRevenue(schedules),
        };
      }) ?? null;
  },
  appendSchedule(
    lineItemId: string,
    schedule: ContractLineItem['schedules'][number],
  ): void {
    lineItemsState.data =
      lineItemsState.data?.map((item) => {
        if (item.id !== lineItemId) return item;
        const schedules = [...item.schedules, schedule];
        return {
          ...item,
          schedules,
          schedule_revenue_total: sumScheduleRevenue(schedules),
        };
      }) ?? null;
  },
  removeSchedule(lineItemId: string, scheduleId: string): void {
    lineItemsState.data =
      lineItemsState.data?.map((item) => {
        if (item.id !== lineItemId) return item;
        const schedules = item.schedules.filter((s) => s.id !== scheduleId);
        return {
          ...item,
          schedules,
          schedule_revenue_total: sumScheduleRevenue(schedules),
        };
      }) ?? null;
  },
  clear(): void {
    lineItemsToken++;
    lineItemsState.data = null;
    lineItemsState.error = null;
    lineItemsState.loading = false;
    lineItemsState.loadedContractId = null;
  },
};

export async function createContract(body: ContractCreateRequest): Promise<Contract> {
  return parseContract(await createContractApi(body));
}

export async function patchContract(id: string, body: ContractPatchRequest): Promise<Contract> {
  const updated = parseContract(await patchContractApi(id, body));
  if (detailState.data?.id === id) detailState.data = updated;
  return updated;
}

export async function archiveContract(id: string, reason: string): Promise<Contract> {
  const updated = parseContract(await archiveContractApi(id, { reason }));
  if (detailState.data?.id === id) detailState.data = updated;
  return updated;
}

export async function restoreContract(id: string, reason: string | null): Promise<Contract> {
  const updated = parseContract(await restoreContractApi(id, { reason }));
  if (detailState.data?.id === id) detailState.data = updated;
  return updated;
}

export async function transitionContract(
  id: string,
  body: TransitionContractRequest,
): Promise<Contract> {
  const updated = parseContract(await transitionContractApi(id, body));
  if (detailState.data?.id === id) detailState.data = updated;
  return updated;
}
